import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { callPostApi } from "../../function/ApiCallFunction";
import { Button, Box, InputAdornment, TextField, Typography } from "@mui/material";
import { ArrowBack, MailOutline } from "@mui/icons-material";
import { toast } from "react-toastify";
import { DataContext } from "../../context/DataContext";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";
import { CASHI_TAGLINE, cashiLogo } from "../../include/cashiBrand";

const RESEND_SECONDS = 45;

const emailFieldSx = {
  "& .MuiInputLabel-root": { fontSize: 13, fontWeight: 650, color: consoleColors.ink, mb: 0.5, position: "static", transform: "none" },
  "& .MuiInputLabel-root.Mui-focused": { color: consoleColors.accent },
  "& .MuiInputBase-root": { borderRadius: 2.5, background: "#f8fafc", mt: 0.4 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: consoleColors.line },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "& .MuiOutlinedInput-root.Mui-focused": { background: "#fff" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: consoleColors.accent, borderWidth: 1.5 },
  "& .MuiOutlinedInput-notchedOutline legend": { display: "none" },
  "& .MuiInputBase-input": { fontSize: 16, py: 1.45, letterSpacing: 0.2 },
  "& .MuiFormHelperText-root": { mx: 0, mt: 1, fontSize: 13 },
};

function OtpBoxes({ value, onChange, disabled }) {
  const refs = useRef([]);
  const digits = String(value || "").replace(/\D/g, "").slice(0, 6).split("");

  useEffect(() => {
    refs.current[String(value || "").length < 6 ? String(value || "").length : 5]?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!value) refs.current[0]?.focus();
  }, [value]);

  const setAt = (index, raw) => {
    const char = String(raw || "").replace(/\D/g, "").slice(-1);
    const next = Array.from({ length: 6 }, (_, i) => digits[i] || "");
    next[index] = char;
    const code = next.join("");
    onChange(code);
    if (char && index < 5) refs.current[index + 1]?.focus();
  };

  const onKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      const next = Array.from({ length: 6 }, (_, i) => digits[i] || "");
      if (next[index]) {
        next[index] = "";
        onChange(next.join(""));
        return;
      }
      if (index > 0) {
        next[index - 1] = "";
        onChange(next.join(""));
        refs.current[index - 1]?.focus();
      }
      return;
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      refs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight" && index < 5) {
      event.preventDefault();
      refs.current[index + 1]?.focus();
    }
  };

  const onPaste = (event) => {
    event.preventDefault();
    const pasted = String(event.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    onChange(pasted);
    refs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <Box sx={{ display: "flex", gap: { xs: 0.8, sm: 1.1 }, justifyContent: "space-between" }} onPaste={onPaste}>
      {Array.from({ length: 6 }, (_, index) => (
        <Box
          key={index}
          component="input"
          ref={(node) => { refs.current[index] = node; }}
          value={digits[index] || ""}
          disabled={disabled}
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={1}
          aria-label={`Digit ${index + 1}`}
          onChange={(event) => setAt(index, event.target.value)}
          onKeyDown={(event) => onKeyDown(index, event)}
          onFocus={(event) => event.target.select()}
          sx={{
            width: { xs: 44, sm: 52 },
            height: { xs: 52, sm: 58 },
            borderRadius: 2,
            border: "1.5px solid",
            borderColor: digits[index] ? consoleColors.accent : consoleColors.line,
            background: "#fff",
            textAlign: "center",
            fontSize: 22,
            fontWeight: 750,
            color: consoleColors.ink,
            outline: "none",
            caretColor: consoleColors.accent,
            "&:focus": { borderColor: consoleColors.accent, boxShadow: "0 0 0 3px rgba(33,167,236,0.18)" },
            "&:disabled": { background: "#f8fafc" },
          }}
        />
      ))}
    </Box>
  );
}

function Login({ onLogin }) {
  const navigate = useNavigate();
  const { setLoginData } = useContext(DataContext);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [wait, setWait] = useState(0);
  const submitted = useRef("");

  useEffect(() => {
    if (wait < 1) return undefined;
    const timer = setTimeout(() => setWait((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);

  const sendCode = async (event) => {
    event?.preventDefault();
    if (!email.trim()) { toast.error("Email is required"); return; }
    if (wait > 0) return;
    setLoading(true);
    try {
      const result = await callPostApi("store-login", { mod: "STORE_SEND_OTP", data_arr: { email: email.trim() } });
      if (result.status === "200") {
        setSentTo(result.data?.email || email.trim());
        setOtp("");
        submitted.current = "";
        setWait(RESEND_SECONDS);
        toast.success(result.data?.message || "A 6-digit code was sent to this email.");
      } else toast.error(result?.status_message || result?.data?.error || "Could not send the code", { autoClose: 2500 });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (code = otp) => {
    const pin = String(code || "").replace(/\D/g, "");
    if (!/^\d{6}$/.test(pin)) { toast.error("Enter the 6-digit code"); return; }
    if (loading || submitted.current === pin) return;
    submitted.current = pin;
    setLoading(true);
    try {
      const result = await callPostApi("store-login", { mod: "STORE_VERIFY_OTP", data_arr: { email: sentTo || email.trim(), otp: pin } });
      if (result.status === "200" && result.data) {
        toast.success("Login successfully!", { autoClose: 1000 });
        setLoginData(result.data);
        sessionStorage.setItem("loginData", JSON.stringify(result.data));
        onLogin(result.data);
      } else {
        submitted.current = "";
        toast.error(result?.status_message || result?.data?.error || "That code is not correct", { autoClose: 2500 });
      }
    } catch {
      submitted.current = "";
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (/^\d{6}$/.test(otp)) verifyCode(otp);
  }, [otp]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", background: "#fff" }}>
      <Box sx={{ display: { xs: "none", md: "flex" }, width: "46%", background: consoleColors.sidebar, color: "#fff", flexDirection: "column", justifyContent: "space-between", px: 7, py: 6 }}>
        <Box>
          <Box component="img" src={cashiLogo} alt="Cashi" sx={{ height: 42, width: "auto" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 1.4, color: consoleColors.accent, mt: 5 }}>STORE LOGIN</Typography>
          <Typography sx={{ fontSize: 34, fontWeight: 750, lineHeight: 1.2, mt: 1.2, maxWidth: 420 }}>
            {CASHI_TAGLINE}
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: 1.5, mt: 2, maxWidth: 400, color: consoleColors.navText }}>
            Sign in with your store email. A 6-digit code is sent to that inbox — no password to remember.
          </Typography>
          <Box sx={{ mt: 4, display: "grid", gap: 1.4 }}>
            {["Register the bill and loyalty in one step", "Share club points across member stores", "Lucky Draw Coupons and printed cards"].map((line) => (
              <Box key={line} sx={{ display: "flex", gap: 1.2, alignItems: "flex-start" }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: consoleColors.accent, mt: 0.7, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 14.5, color: consoleColors.navText, lineHeight: 1.45 }}>{line}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Typography sx={{ color: consoleColors.navMuted, fontSize: 13 }}>store.cashi.in</Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: { xs: 2, sm: 3 }, background: "linear-gradient(180deg, #f3f6f9 0%, #eef4f8 100%)" }}>
        <Box
          component="form"
          onSubmit={(event) => { event.preventDefault(); sentTo ? verifyCode() : sendCode(event); }}
          sx={{
            width: "100%",
            maxWidth: 440,
            background: "#fff",
            border: `1px solid ${consoleColors.line}`,
            borderRadius: 3.5,
            p: { xs: 2.5, sm: 4 },
            boxShadow: "0 18px 50px rgba(15,23,42,0.06)",
          }}
        >
          <Box sx={{ display: { xs: "block", md: "none" }, mb: 2.5 }}>
            <Box component="img" src={cashiLogo} alt="Cashi" sx={{ height: 30, width: "auto" }} />
          </Box>

          <Box sx={{ display: "flex", gap: 0.8, mb: 2 }}>
            {["Email", "Code"].map((label, index) => {
              const active = sentTo ? index === 1 : index === 0;
              return (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                  <Box sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    fontSize: 11,
                    fontWeight: 750,
                    display: "grid",
                    placeItems: "center",
                    background: active ? consoleColors.accent : "#eef2f6",
                    color: active ? "#fff" : consoleColors.muted,
                  }}
                  >
                    {index + 1}
                  </Box>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 650, color: active ? consoleColors.ink : consoleColors.muted }}>{label}</Typography>
                  {index === 0 && <Box sx={{ width: 18, height: 1, background: consoleColors.line, mx: 0.4 }} />}
                </Box>
              );
            })}
          </Box>

          <Typography sx={{ fontSize: 13, color: consoleColors.muted, fontWeight: 700, letterSpacing: 0.7 }}>STORE</Typography>
          <Typography sx={{ fontSize: 28, fontWeight: 750, color: consoleColors.ink, mt: 0.4, letterSpacing: -0.4 }}>
            {sentTo ? "Enter the code" : "Sign in"}
          </Typography>

          {sentTo ? (
            <>
              <Typography sx={{ color: consoleColors.muted, fontSize: 14.5, mt: 1, mb: 2.2, lineHeight: 1.5 }}>
                We sent a 6-digit code to
                <Box component="span" sx={{ display: "block", color: consoleColors.ink, fontWeight: 700, mt: 0.25 }}>{sentTo}</Box>
              </Typography>
              <OtpBoxes value={otp} onChange={setOtp} disabled={loading} />
              <Button type="submit" fullWidth disabled={loading || otp.length < 6} variant="contained" sx={{ ...primaryButtonSx, mt: 3, py: 1.35, borderRadius: 2, fontSize: 15.5 }}>
                {loading ? "Signing in..." : "Sign in"}
              </Button>
              <Button type="button" fullWidth disabled={loading || wait > 0} onClick={sendCode} sx={{ mt: 1.1, textTransform: "none", color: consoleColors.accent, fontWeight: 650 }}>
                {wait > 0 ? `Send a new code in ${wait}s` : "Send a new code"}
              </Button>
              <Button
                type="button"
                fullWidth
                startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
                onClick={() => { setSentTo(""); setOtp(""); submitted.current = ""; }}
                sx={{ mt: 0.2, textTransform: "none", color: consoleColors.muted }}
              >
                Use a different email
              </Button>
            </>
          ) : (
            <>
              <Typography sx={{ color: consoleColors.muted, fontSize: 14.5, mt: 1, mb: 2.4, lineHeight: 1.5 }}>
                Enter the store email. We will send a 6-digit login code.
              </Typography>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="store@email.com"
                helperText="Use the email saved on this store or staff login."
                autoComplete="email"
                autoFocus
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutline sx={{ fontSize: 20, color: consoleColors.muted }} />
                    </InputAdornment>
                  ),
                }}
                sx={emailFieldSx}
              />
              <Button type="submit" fullWidth disabled={loading} variant="contained" sx={{ ...primaryButtonSx, mt: 2.6, py: 1.35, borderRadius: 2, fontSize: 15.5 }}>
                {loading ? "Sending..." : "Send code"}
              </Button>
              <Button
                type="button"
                fullWidth
                disabled={loading}
                onClick={() => {
                  if (!email.trim()) { toast.error("Email is required"); return; }
                  setSentTo(email.trim());
                  setOtp("");
                  submitted.current = "";
                }}
                sx={{ mt: 1.1, textTransform: "none", color: consoleColors.accent, fontWeight: 650 }}
              >
                I already have a code
              </Button>
              <Button
                type="button"
                fullWidth
                onClick={() => navigate("/storeregister")}
                sx={{ mt: 1.1, py: 1.15, textTransform: "none", fontWeight: 650, color: consoleColors.ink, border: `1px solid ${consoleColors.line}`, borderRadius: 2 }}
              >
                New store? Sign up
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default Login;
